/**
 * Cost Charts Component
 * Displays the By Company, By State, and By Category charts
 */
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building, MapPin, Filter } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

// Chart configuration constants
const CHART_MARGIN = { left: 0, right: 10 };
const AXIS_TICK_STYLE = { fontSize: 10 };

const CostCharts = ({ 
  companyChartData, 
  stateChartData, 
  categoryPieData,
  formatCurrency,
  getPeriodMultiplier
}) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* By Company */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Building className="w-4 h-4 text-muted-foreground" />
            Cost by Company
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[200px]">
            {companyChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={companyChartData} layout="vertical" margin={CHART_MARGIN}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="name" width={70} tick={AXIS_TICK_STYLE} tickLine={false} axisLine={false} />
                  <Tooltip 
                    formatter={(v) => formatCurrency(v / getPeriodMultiplier())}
                    labelFormatter={(label) => companyChartData.find(d => d.name === label)?.fullName || label}
                  />
                  <Bar dataKey="Risk" stackId="a" fill="#EF4444" name="Risk" />
                  <Bar dataKey="Overpay" stackId="a" fill="#F59E0B" name="Overpay" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">No data</div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* By State */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <MapPin className="w-4 h-4 text-muted-foreground" />
            Cost by State (Top 10)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[200px]">
            {stateChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stateChartData} layout="vertical" margin={CHART_MARGIN}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="name" width={70} tick={AXIS_TICK_STYLE} tickLine={false} axisLine={false} />
                  <Tooltip formatter={(v) => formatCurrency(v / getPeriodMultiplier())} />
                  <Bar dataKey="Risk" stackId="a" fill="#EF4444" name="Risk" />
                  <Bar dataKey="Overpay" stackId="a" fill="#F59E0B" name="Overpay" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">No data</div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* By Category */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            Cost by Category
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[200px]">
            {categoryPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoryPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {categoryPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => formatCurrency(v / getPeriodMultiplier())} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">No data</div>
            )}
          </div>
          {categoryPieData.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2 justify-center">
              {categoryPieData.slice(0, 4).map((cat) => (
                <div key={cat.name} className="flex items-center gap-1 text-xs">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                  <span className="truncate max-w-[60px]">{cat.name}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CostCharts;
